'use client';

import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackPageDuration } from '@/src/utils/analytics';

import { useAppContext } from '@/lib/app-context';
import { useMediaQuery } from '@/lib/hooks/use-media-query';
import { CommunityRightSidebar } from './community-right-sidebar';

import { usePostDetail } from '@/lib/hooks/use-post-detail';
import { PostDetailContent } from './post-detail-content';
import { PublishModal } from './publish-modal';
import './post-detail.less';

/**
 * PostDetailPage — standalone page for direct URL access to /community/post/[id].
 * Uses global TopBar; renders three-column layout (left sidebar + detail + right sidebar).
 * Clicking sidebar boards navigates to the community page with board filter.
 */
export function PostDetailPage({ postId }: { postId: string }) {
	const navigate = useNavigate();
	const { setWaveIntensity } = useAppContext();
	const hideRightSidebar = useMediaQuery('(max-width: 1023px)');
	const { post, loading, handlers, panelStates, refreshPost } = usePostDetail(postId);

	useEffect(() => trackPageDuration('community_post'), [postId]);

	const handleEditSuccess = useCallback(() => {
		refreshPost();
		panelStates.setEditModalOpen(false);
	}, [refreshPost, panelStates]);

	useEffect(() => {
		setWaveIntensity(0.2);
		return () => setWaveIntensity(1);
	}, [setWaveIntensity]);

	const handleTagClick = useCallback(
		(tag: string | null) => {
			if (tag) {
				navigate(`/community?tag=${tag}`);
			} else {
				navigate('/community');
			}
		},
		[navigate],
	);

	return (
		<div className="h-full w-full overflow-y-auto pt-14">
			<div className="max-w-[1400px] mx-auto flex gap-5 px-4 sm:px-6 py-4">
				{/* ── Center: Post detail ── */}
				<main className="flex-1 min-w-0">
					<div className="liquid-glass-card no-hover rounded-2xl overflow-hidden">
						<PostDetailContent handlers={handlers} loading={loading} panelStates={panelStates} post={post} />
					</div>
				</main>

				{/* ── Right sidebar — sticky aside ── */}
				{!hideRightSidebar && (
					<aside className="w-[260px] shrink-0">
						<div className="sticky top-4">
							<CommunityRightSidebar activeTag={null} onTagClick={handleTagClick} />
						</div>
					</aside>
				)}
			</div>

			{/* ── Edit modal ── */}
			{post && <PublishModal editPost={post} onClose={() => panelStates.setEditModalOpen(false)} onSuccess={handleEditSuccess} open={panelStates.editModalOpen} />}
		</div>
	);
}
